import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import Modal from 'react-modal';
import Swal from 'sweetalert2';

import HeaderUno from '../../Metodos/Compartidos/HeaderUno.js';
import HeaderDos from '../../Metodos/Compartidos/HeaderDos.js';
import useConfigAuth from '../../../Hooks/useConfigAuth.js';

import { cargarTurnosAction } from '../../../actions/cargarTurnosActions.js';
import {cargarPacientesAction} from '../../../actions/cargarPacientesActions.js';
import { crearTurnoAction } from '../../../actions/crearTurnoActions.js';
import { editarTurnoAction } from '../../../actions/editarTurnoActions.js';
import { borrarTurnoAction } from '../../../actions/borrarTurnoActions.js';
import { reenviarNotificacionTurnoAction } from '../../../actions/reenviarNotificacionTurnoActions.js';
import {cargarConsultoriosProfesionalAction} from '../../../actions/consultoriosPerfilActions.js';
import { setearIdPacienteAction } from '../../../actions/setearIdPacienteActions.js';
import { resetearConfigPacienteAction } from '../../../actions/resetStoreActions.js';

import {
  cargarConfiguracionCalendarioAction,
  guardarConfiguracionCalendarioAction
} from '../../../actions/configuracionCalendarioActions.js';

import { Cuerpo } from '../../Css/StyledHeader.js';

import {
  CalendarWrapper,
  CalendarContainer,
  CalendarSection,
  CalendarMainContainer
} from '../../Css/StyledCalendarBase.js';

import CalendarHeader from './CalendarHeader.js';
import CalendarModal from './CalendarModal.js';
import CalendarSettingsModal from './CalendarSettingsModal.js';

import { MonthView, WeekView, DayView } from './CalendarViews/index.js';
import MobileWeekView from './CalendarViews/MobileWeekView.js';
import MobileMonthView from './CalendarViews/MobileMonthView.js';

import {
  VIEW_TYPES,
  WEEKDAY_LABELS,
  WEEKDAY_SHORT_LABELS,
  normalizeTurnType
} from '../../../constants/calendarConstants.js';

import {
  formatMonthYear,
  formatWeekRange,
  getStartOfWeek,
  generateMonthDays,
  shiftDateByDays,
  shiftDateByMonths,
  getAppointmentsForDate,
  getAppointmentsForWeek,
  sortAppointmentsByTime,
} from '../../../utils/calendarUtils.js';

Modal.setAppElement('#root');

// Normaliza el valor de repetición del turno.
// El frontend trabaja con "semanal", "quincenal" o "no".
const normalizeRepeatValue = (repeatValue) => {
  if (repeatValue === 'semanalmente') {
    return 'semanal';
  }

  if (repeatValue === 'quincenalmente') {
    return 'quincenal';
  }

  return repeatValue || 'no';
};

// Normaliza la modalidad del turno.
// Si no viene "virtual", asumimos presencial.
const normalizeModalidadTurno = (modalidad) => {
  if (modalidad === 'virtual') {
    return 'virtual';
  }

  return 'presencial';
};

// Convierte fecha y hora a objeto Date local.
// Se usa para saber si un turno ya comenzó o ya pasó.
const parseLocalDateTime = (dateString, timeString) => {
  if (!dateString || !timeString) {
    return null;
  }

  const [year, month, day] = dateString.split('-').map(Number);
  const [hours, minutes] = timeString.split(':').map(Number);

  if (
    !year ||
    !month ||
    !day ||
    Number.isNaN(hours) ||
    Number.isNaN(minutes)
  ) {
    return null;
  }

  return new Date(year, month - 1, day, hours, minutes, 0, 0);
};

// Devuelve true si el turno ya empezó o ya pasó.
// Se usa para decidir si se puede borrar físicamente o si debe quedar como registro.
const turnoYaComenzoOPaso = ({ fecha_turno, hora_inicio }) => {
  const fechaHoraTurno = parseLocalDateTime(fecha_turno, hora_inicio);

  if (!fechaHoraTurno) {
    return false;
  }

  const ahora = new Date();

  return fechaHoraTurno <= ahora;
};

// Devuelve true si el turno tiene una notificación que ya fue procesada.
// En ese caso no conviene ofrecer borrado físico desde el frontend.
const tieneNotificacionProcesada = (turno) => {
  const estadoNotificacion = turno?.estado_notificacion || 'SIN_NOTIFICACION';

  return (
    estadoNotificacion === 'ENVIADA' ||
    estadoNotificacion === 'ENTREGADO' ||
    estadoNotificacion === 'REBOTADO' ||
    estadoNotificacion === 'SPAM' ||
    estadoNotificacion === 'DEMORADO' ||
    estadoNotificacion === 'ERROR' ||
    estadoNotificacion === 'REINTENTO_PENDIENTE'
  );
};

const Calendario = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [config] = useConfigAuth();

  const pacientes = useSelector((state) => state.informacion?.lista_pacientes || []);
  const turnosCalendario = useSelector((state) => state.informacion?.info_calendario || []);

  const consultoriosProfesional = useSelector(
    (state) => state.usuario?.consultorios_profesional || []
  );

  // Configuración visual del calendario.
  // La fuente real es Redux, que se actualiza con GET /usuarios/configuracioncalendario.
  const calendarViewSettings = useSelector(
    (state) => state.usuario?.configuracion_calendario || null
  );

  // Loading de la configuración.
  // Es solo informativo para mostrar el texto correcto.
  const calendarViewSettingsLoading = useSelector(
    (state) => Boolean(state.usuario?.configuracion_calendario_cargando)
  );

  // Normaliza los turnos que vienen del backend para que todas las vistas
  // trabajen con los mismos nombres de campos.
  const appointments = useMemo(() => {
    if (!Array.isArray(turnosCalendario)) {
      return [];
    }

    return turnosCalendario.map((turno) => {
      const pacienteNombreCompleto =
        turno.paciente_nombre_completo ||
        `${turno.paciente_nombre || ''} ${turno.paciente_apellido || ''}`.trim() ||
        'Paciente sin nombre';

      const repeatNormalizado = normalizeRepeatValue(
        turno.repeat_type ||
        turno.repeat ||
        turno.repetir_turno ||
        turno.tipo_repeticion ||
        'no'
      );

      const modalidadTurno = normalizeModalidadTurno(
        turno.modalidad_turno ||
        turno.modalidad ||
        'presencial'
      );

      const telefonoConsultorio =
        turno.telefono_consultorio ||
        turno.telefono_contacto ||
        '';

      const incluirTelefonoConsultorio =
        turno.incluir_telefono_consultorio ||
        turno.incluir_telefono ||
        (telefonoConsultorio ? 'si' : 'no');

      return {
        ...turno,

        id: turno.id || turno.id_turno,
        id_turno: turno.id_turno || turno.id,

        // Datos mínimos de Historia Clínica asociados al turno.
        // Vienen desde GET /paciente/cargarturnos cuando el backend ya creó HC.
        historia_clinica_id:
          turno.historia_clinica_id ||
          turno.hc_id ||
          null,

        tiene_historia_clinica: Boolean(
          turno.tiene_historia_clinica ||
          turno.historia_clinica_id ||
          turno.hc_id
        ),

        estado_historia_clinica:
          turno.estado_historia_clinica ||
          turno.estado_hc ||
          null,

        id_paciente: turno.id_paciente || '',
        paciente_id: turno.paciente_id || turno.id_paciente || '',

        paciente_nombre: turno.paciente_nombre || '',
        paciente_apellido: turno.paciente_apellido || '',
        paciente_nombre_completo: pacienteNombreCompleto,

        fecha_turno: turno.fecha_turno || '',
        hora_inicio: turno.hora_inicio || '',
        hora_fin: turno.hora_fin || '',

        estado: turno.estado || 'PENDIENTE',

        // Tipo de encuentro del turno.
        // Si el backend no lo manda en turnos viejos, lo tratamos como sesión con paciente.
        tipo_turno: normalizeTurnType(turno.tipo_turno),

        comentario: turno.comentario || '',
        link_reunion: turno.link_reunion || '',
        honorarios: turno.honorarios ?? '',

        repeat: repeatNormalizado,
        repeat_type: repeatNormalizado,
        repeat_group_id: turno.repeat_group_id || null,

        es_parte_serie: Boolean(turno.es_parte_serie || turno.repeat_group_id),
        es_turno_original_serie: Boolean(turno.es_turno_original_serie),
        puede_modificar_condiciones_serie: Boolean(
          turno.puede_modificar_condiciones_serie
        ),

        serie_fecha_origen: turno.serie_fecha_origen || null,
        serie_hora_inicio_origen: turno.serie_hora_inicio_origen || null,

        fecha_notificacion: turno.fecha_notificacion || '',
        notificar: turno.notificar || 'no',
        notificar_cantidad: turno.notificar_cantidad ?? 0,
        email_1: turno.email_1 || '',
        email_2: turno.email_2 || '',

        estado_notificacion: turno.estado_notificacion || 'SIN_NOTIFICACION',
        motivo_error_notificacion: turno.motivo_error_notificacion || null,

        intento_notificacion: turno.intento_notificacion ?? null,

        emails_notificacion: Array.isArray(turno.emails_notificacion)
          ? turno.emails_notificacion
          : [],

        modalidad_turno: modalidadTurno,

        consultorio_id:
          turno.consultorio_id !== undefined && turno.consultorio_id !== null
            ? turno.consultorio_id
            : null,

        consultorio_nombre: turno.consultorio_nombre || null,
        consultorio_direccion: turno.consultorio_direccion || null,
        consultorio_telefono: turno.consultorio_telefono || null,
        consultorio_activo:
          turno.consultorio_activo === undefined
            ? null
            : turno.consultorio_activo,

        direccion_consultorio:
          modalidadTurno === 'presencial'
            ? turno.direccion_consultorio || ''
            : '',

        incluir_telefono_consultorio:
          incluirTelefonoConsultorio === 'si' ? 'si' : 'no',

        telefono_consultorio:
          incluirTelefonoConsultorio === 'si'
            ? telefonoConsultorio
            : '',

        cola_email_turnos: Array.isArray(turno.cola_email_turnos)
          ? turno.cola_email_turnos
          : []
      };
    });
  }, [turnosCalendario]);

  const [currentView, setCurrentView] = useState(VIEW_TYPES.WEEK);
  const [currentDate, setCurrentDate] = useState(() => new Date());
  const [selectedDate, setSelectedDate] = useState(() => new Date());
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalPrefilledData, setModalPrefilledData] = useState(null);
  const [modalMode, setModalMode] = useState('create');
  const [appointmentToEdit, setAppointmentToEdit] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);

  const turnosCargadosRef = useRef(false);
  const consultoriosCargadosRef = useRef(false);
  const pacientesCargadosRef = useRef(false);

  // Evita pedir la configuración visual más de una vez por montaje del componente.
  // Igual, cuando se monta Calendario, se pide siempre al backend.
  const configuracionCalendarioCargadaRef = useRef(false);

  const authToken =
    config?.headers?.Authorization ||
    config?.headers?.authorization ||
    null;

  // Formatea un Date a YYYY-MM-DD en horario local.
  // Se usa para prefijar fechas en el modal.
  const formatDateLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  };

  // Calcula una hora de fin estimada a partir de una hora de inicio.
  // Por defecto suma 30 minutos.
  const calculateEndTime = (timeSlot, durationMinutes = 30) => {
    const [hours, minutes] = timeSlot.split(':').map(Number);

    const startTime = new Date();
    startTime.setHours(hours, minutes, 0, 0);

    const endTime = new Date(startTime);
    endTime.setMinutes(endTime.getMinutes() + durationMinutes);

    const endHours = endTime.getHours().toString().padStart(2, '0');
    const endMinutes = endTime.getMinutes().toString().padStart(2, '0');

    return `${endHours}:${endMinutes}`;
  };

  // Arma el payload que espera el backend para crear o editar turnos.
  // Mantiene compatibilidad con campos nuevos como modalidad, honorarios y notificación.
  const buildBackendPayload = (appointmentData, selectedPatientId) => {
    const modalidadTurno = normalizeModalidadTurno(
      appointmentData.modalidad_turno ||
      appointmentData.modalidad ||
      'presencial'
    );

    const notificarTurno = appointmentData.notificar || 'no';

    const incluirTelefonoConsultorio =
      appointmentData.incluir_telefono_consultorio === 'si'
        ? 'si'
        : 'no';

    const payload = {
      id_paciente: selectedPatientId,

      // Nuevo campo de arquitectura Turnos/HC.
      // El turno solo guarda qué tipo de encuentro es; los datos clínicos van luego en HC.
      tipo_turno: normalizeTurnType(appointmentData.tipo_turno),

      modificar_condiciones_serie: appointmentData.modificar_condiciones_serie || false,

      fecha_turno: appointmentData.fecha_turno,
      fecha_turno_desde:
        appointmentData.fecha_turno_original ||
        appointmentData.fecha_turno_desde ||
        appointmentData.fecha_turno,

      hora_inicio: appointmentData.hora_inicio,
      hora_fin: appointmentData.hora_fin,

      fecha_notificacion: appointmentData.fecha_notificacion || '',

      link_reunion:
        modalidadTurno === 'virtual'
          ? appointmentData.link_reunion || ''
          : '',

      honorarios: appointmentData.honorarios ?? '',

      email_1: appointmentData.email_1 || '',
      email_2: appointmentData.email_2 || '',
      notificar: notificarTurno,
      notificar_cantidad: appointmentData.notificar_cantidad ?? 0,

      modalidad_turno: modalidadTurno,

      direccion_consultorio:
        modalidadTurno === 'presencial' && notificarTurno === 'si'
          ? appointmentData.direccion_consultorio || ''
          : '',

      incluir_telefono_consultorio: incluirTelefonoConsultorio,

      telefono_consultorio:
        incluirTelefonoConsultorio === 'si'
          ? appointmentData.telefono_consultorio || ''
          : '',

      comentario: appointmentData.comentario || '',
      estado: appointmentData.estado || 'PENDIENTE',

      aplicar_serie_desde_este_turno:
        appointmentData.aplicar_serie_desde_este_turno || false,

      repeat_group_id: appointmentData.repeat_group_id || null
    };

    const idTurno = appointmentData.id_turno || appointmentData.id;

    if (idTurno) {
      payload.id_turno = idTurno;
    }

    const shouldSendRepeat =
      appointmentData.mode === 'create' ||
      appointmentData.modificar_condiciones_serie;

    if (shouldSendRepeat) {
      const repeatNormalizado = normalizeRepeatValue(
        appointmentData.repeat_type || appointmentData.repeat
      );

      payload.repeat = repeatNormalizado;
      payload.repeat_type = repeatNormalizado;
    }
    if (Object.prototype.hasOwnProperty.call(appointmentData, 'consultorio_id')) {
      payload.consultorio_id =
        modalidadTurno === 'presencial' && appointmentData.consultorio_id
          ? Number(appointmentData.consultorio_id)
          : null;
    }

    return payload;
  };

  // Carga la configuración visual del calendario desde backend.
  // La fuente real es GET /usuarios/configuracioncalendario.
  const loadCalendarViewSettings = async () => {
    if (!config) {
      return;
    }

    try {
      await dispatch(
        cargarConfiguracionCalendarioAction({ config })
      );

    } catch (error) {
      console.error('Error al cargar configuración del calendario:', error);

      const mensaje =
        error.userMessage ||
        'No se pudo cargar la configuración del calendario.';

      await Swal.fire({
        icon: 'warning',
        title: 'Configuración del calendario',
        text: mensaje,
        confirmButtonText: 'OK'
      });
    }
  };

  // Carga los turnos desde el backend.
  const loadAppointments = async () => {
    if (!config) {
      return;
    }

    try {
      await dispatch(cargarTurnosAction({ config }));
    } catch (error) {
      console.error('Error al cargar turnos:', error);
    }
  };

  const loadPatients = async () => {
    if (!config) {
      return;
    }

    try {
      await dispatch(
        cargarPacientesAction({
          config
        })
      );
    } catch (error) {
      console.error(
        'Error al cargar pacientes:',
        error
      );
    }
  };

  const loadConsultorios = async () => {
    if (!config) {
      return;
    }

    try {
      await dispatch(
        cargarConsultoriosProfesionalAction({
          config
        })
      );
    } catch (error) {
      console.error('Error al cargar consultorios:', error);
    }
  };

  // Detecta si la pantalla está en modo mobile.
  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      setIsMobile(width <= 480);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);

    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Si la vista actual es día, sincroniza selectedDate con currentDate.
  useEffect(() => {
    if (currentView === VIEW_TYPES.DAY) {
      setSelectedDate(currentDate);
    }
  }, [currentView, currentDate]);

  // Cuando tenemos token, cargamos los datos generales
  // necesarios para trabajar desde Calendario.
  //
  // Calendario es la pantalla principal,
  // por eso dejamos disponibles desde acá:
  //
  // - configuración visual;
  // - consultorios;
  // - turnos;
  // - pacientes.
  //
  // De esta forma, si el profesional entra directamente
  // a Estadísticas y cobros → Centros y liquidaciones,
  // la lista de pacientes ya está disponible en Redux.

  useEffect(() => {

    if (!authToken) {
      return;
    }


    /*
    ====================================
    CONFIGURACIÓN DEL CALENDARIO
    ====================================
    */

    if (!configuracionCalendarioCargadaRef.current) {

      configuracionCalendarioCargadaRef.current = true;

      loadCalendarViewSettings();

    }


    /*
    ====================================
    CONSULTORIOS
    ====================================
    */

    if (!consultoriosCargadosRef.current) {

      consultoriosCargadosRef.current = true;

      loadConsultorios();

    }


    /*
    ====================================
    TURNOS
    ====================================
    */

    if (!turnosCargadosRef.current) {

      turnosCargadosRef.current = true;

      loadAppointments();

    }


    /*
    ====================================
    PACIENTES
    ====================================
    */

    if (!pacientesCargadosRef.current) {

      pacientesCargadosRef.current = true;

      loadPatients();

    }

  }, [authToken]);

  const monthDays = useMemo(() => generateMonthDays(currentDate), [currentDate]);
  const totalRows = Math.ceil(monthDays.length / 7);
  const highlightDay = currentDate.getDate();

  // Arma los días de la semana actual.
  const weekDays = useMemo(() => {
    const startOfWeek = getStartOfWeek(currentDate);

    return WEEKDAY_LABELS.map((name, index) => {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + index);

      return {
        name,
        shortLabel: WEEKDAY_SHORT_LABELS[index],
        dateNumber: date.getDate(),
        date,
      };
    });
  }, [currentDate]);

  const weekRangeLabel = useMemo(
    () => formatWeekRange(weekDays[0]?.date, weekDays[6]?.date),
    [weekDays],
  );

  const daySelectorDays = useMemo(
    () =>
      weekDays.map((day) => ({
        label: day.shortLabel,
        dateNumber: day.dateNumber,
        date: day.date,
      })),
    [weekDays],
  );

  const currentMonthLabel = useMemo(
    () => formatMonthYear(currentDate),
    [currentDate]
  );

  const selectedDayNumber = selectedDate.getDate();

  // Turnos del día seleccionado.
  const dayAppointments = useMemo(() => {
    const dayAppts = getAppointmentsForDate(appointments, selectedDate);
    return sortAppointmentsByTime(dayAppts);
  }, [selectedDate, appointments]);

  // Turnos de la semana visible.
  const weekAppointments = useMemo(() => {
    const startOfWeek = getStartOfWeek(currentDate);
    const endOfWeek = new Date(startOfWeek);

    endOfWeek.setDate(startOfWeek.getDate() + 6);

    const weekAppts = getAppointmentsForWeek(
      appointments,
      startOfWeek,
      endOfWeek
    );

    return sortAppointmentsByTime(weekAppts);
  }, [currentDate, appointments]);

  // Cambia el día seleccionado.
  const handleSelectDate = (date) => {
    const nextDate = new Date(date);

    setSelectedDate(nextDate);
    setCurrentDate(nextDate);
  };

  // Cambia el estado de un turno desde las vistas.
  // Si el estado pasa a CANCELADO, REALIZADO o NO_ASISTIO,
  // mandamos la notificación apagada para que backend cancele pendientes.
  const handleStatusChange = async (appointmentId, newStatus) => {
    const appointment = appointments.find((appointmentItem) => {
      const currentId = appointmentItem.id_turno || appointmentItem.id;
      return String(currentId) === String(appointmentId);
    });

    if (!appointment) {
      alert('No se encontró el turno para modificar el estado.');
      return;
    }

    const selectedPatientId =
      appointment.id_paciente ||
      appointment.paciente_id;

    if (!selectedPatientId) {
      alert('No se encontró el paciente del turno.');
      return;
    }

    const estadoApagaNotificacion =
      newStatus === 'CANCELADO' ||
      newStatus === 'REALIZADO' ||
      newStatus === 'NO_ASISTIO';

    const payloadBackend = buildBackendPayload(
      {
        ...appointment,
        estado: newStatus,
        notificar: estadoApagaNotificacion ? 'no' : appointment.notificar,
        fecha_notificacion: estadoApagaNotificacion ? '' : appointment.fecha_notificacion,
        notificar_cantidad: estadoApagaNotificacion ? 0 : appointment.notificar_cantidad,
        email_1: estadoApagaNotificacion ? '' : appointment.email_1,
        email_2: estadoApagaNotificacion ? '' : appointment.email_2
      },
      selectedPatientId
    );

    const informacion = {
      config,
      payload: payloadBackend
    };

    try {
      const turnoEditado = await dispatch(editarTurnoAction(informacion));

      if (!turnoEditado) {
        await Swal.fire({
          icon: 'error',
          title: 'No se pudo modificar el estado',
          text: 'El backend no devolvió confirmación.',
          confirmButtonText: 'OK',
          allowOutsideClick: false,
          allowEscapeKey: false
        });

        return;
      }

    } catch (error) {
      const mensaje =
        error.userMessage ||
        error.response?.data?.msg ||
        error.response?.data?.message ||
        error.response?.data?.error ||
        'No se pudo modificar el estado del turno';

      await Swal.fire({
        icon: 'error',
        title: 'No se pudo modificar el estado',
        text: mensaje,
        confirmButtonText: 'OK',
        allowOutsideClick: false,
        allowEscapeKey: false
      });
    }
  };

  // Lleva el calendario al día actual.
  const handleGoToToday = () => {
    handleSelectDate(new Date());
  };

  // Cambia período según la vista activa.
  // Día: mueve un día.
  // Semana: mueve siete días.
  // Mes: mueve un mes.
  const handleChangePeriod = (step) => {
    if (currentView === VIEW_TYPES.DAY) {
      handleSelectDate(shiftDateByDays(selectedDate, step));
      return;
    }

    if (currentView === VIEW_TYPES.WEEK) {
      // Cambiamos la semana visible, pero no movemos selectedDate.
      // Así el resaltado de HOY no se arrastra falsamente a la semana siguiente.
      setCurrentDate((prev) => shiftDateByDays(prev, step * 7));
      return;
    }

    setCurrentDate((prev) => shiftDateByMonths(prev, step));
  };

  const handlePrevPeriod = () => handleChangePeriod(-1);
  const handleNextPeriod = () => handleChangePeriod(1);

  // Abre el modal de creación sin fecha ni horario prefijado.
  const openModal = () => {
    setModalMode('create');
    setAppointmentToEdit(null);
    setModalPrefilledData(null);
    setIsModalOpen(true);
  };

  // Abre el modal de creación con una fecha seleccionada.
  // Se usa desde la vista Día cuando el usuario toca el botón +.
  const openCreateModalForDate = (date) => {
    const formattedDate = formatDateLocal(date);

    setModalMode('create');
    setAppointmentToEdit(null);

    setModalPrefilledData({
      day: formattedDate,
      startTime: '',
      endTime: ''
    });

    setIsModalOpen(true);
  };

  // Cierra el modal de turno y limpia estados temporales.
  const closeModal = () => {
    setIsModalOpen(false);
    setModalPrefilledData(null);
    setAppointmentToEdit(null);
    setModalMode('create');
  };

  // Abre modal de configuración visual del calendario.
  // No permitimos abrirlo hasta tener la configuración real del backend.
  const openSettingsModal = async () => {
    if (!calendarViewSettings) {
      await Swal.fire({
        icon: 'info',
        title: 'Configuración cargando',
        text: 'Esperá unos segundos y volvé a intentarlo.',
        confirmButtonText: 'OK'
      });

      return;
    }

    setIsSettingsModalOpen(true);
  };

  // Cierra modal de configuración visual.
  const closeSettingsModal = () => {
    setIsSettingsModalOpen(false);
  };

  // Guarda la configuración visual en backend.
  // Si guarda bien, la action actualiza Redux.
  // El modal recibe true para poder cerrarse.
  const handleSaveCalendarViewSettings = async (settings) => {
    if (!config) {
      await Swal.fire({
        icon: 'error',
        title: 'No se pudo guardar la configuración',
        text: 'No se encontró la configuración de autenticación.',
        confirmButtonText: 'OK'
      });

      return false;
    }

    try {
      await dispatch(
        guardarConfiguracionCalendarioAction({
          config,
          payload: settings
        })
      );

      return true;

    } catch (error) {
      console.error('Error al guardar configuración del calendario:', error);

      const mensaje =
        error.userMessage ||
        'No se pudo guardar la configuración del calendario.';

      await Swal.fire({
        icon: 'error',
        title: 'No se pudo guardar la configuración',
        text: mensaje,
        confirmButtonText: 'OK'
      });

      return false;
    }
  };

  const setearPacienteActualDesdeTurno = (appointment) => {
    if (!appointment) {
      return null;
    }

    const idPaciente =
      appointment.id_paciente ||
      appointment.paciente_id ||
      '';

    if (!idPaciente) {
      return null;
    }

    const pacienteEncontrado = pacientes.find((paciente) => {
      return String(paciente.id_paciente) === String(idPaciente);
    });

    const pacienteParaRedux = {
      id: pacienteEncontrado?.id_paciente || idPaciente,
      nombre:
        pacienteEncontrado?.nombre ||
        appointment.paciente_nombre ||
        appointment.paciente_nombre_completo ||
        '',
      apellido:
        pacienteEncontrado?.apellido ||
        appointment.paciente_apellido ||
        ''
    };

    dispatch(resetearConfigPacienteAction());

    dispatch(
      setearIdPacienteAction({
        payload: pacienteParaRedux
      })
    );

    return pacienteParaRedux;
  };

  // Navega desde un turno del calendario hacia la pantalla actual de Historia Clínica.
  // Pasamos los ids por location.state para no cambiar la URL ni romper rutas existentes.
  const handleOpenClinicalHistory = async (appointment) => {
    const idTurno = appointment?.id_turno || appointment?.id;

    const idPaciente =
      appointment?.id_paciente ||
      appointment?.paciente_id ||
      '';

    const historiaClinicaId =
      appointment?.historia_clinica_id ||
      appointment?.hc_id ||
      null;

    if (!idPaciente || !idTurno || !historiaClinicaId) {
      await Swal.fire({
        icon: 'warning',
        title: 'Historia clínica no disponible',
        text: 'Este turno todavía no tiene una historia clínica asociada.',
        confirmButtonText: 'OK'
      });

      return;
    }

    setearPacienteActualDesdeTurno(appointment);

    closeModal();

    navigate('/escritorio/pacientes/paciente/historiaclinica', {
      state: {
        origen: 'CALENDARIO',
        id_paciente: idPaciente,
        historia_clinica_id: historiaClinicaId,
        id_turno: idTurno
      }
    });
  };

  const handleOpenPatientStats = async (appointment) => {
    const idTurno = appointment?.id_turno || appointment?.id;

    const idPaciente =
      appointment?.id_paciente ||
      appointment?.paciente_id ||
      '';

    if (!idPaciente) {
      await Swal.fire({
        icon: 'warning',
        title: 'Paciente no disponible',
        text: 'No se pudo identificar el paciente asociado a este turno.',
        confirmButtonText: 'OK'
      });

      return;
    }

    setearPacienteActualDesdeTurno(appointment);

    closeModal();

    navigate('/escritorio/pacientes/paciente/estadisticas', {
      state: {
        origen: 'CALENDARIO',
        id_paciente: idPaciente,
        id_turno: idTurno || null
      }
    });
  };

  // Abre modal de edición de turno.
  const openEditModal = (appointment) => {
    setModalMode('edit');
    setAppointmentToEdit(appointment);

    setModalPrefilledData({
      day: appointment.fecha_turno || '',
      startTime: appointment.hora_inicio || '',
      endTime: appointment.hora_fin || ''
    });

    setIsModalOpen(true);
  };

  // Crea un turno nuevo.
  const handleCreateAppointment = async (appointmentData) => {
    const selectedPatientId =
      appointmentData.id_paciente ||
      appointmentData.id_patiente;

    if (!selectedPatientId) {
      alert('Por favor selecciona un paciente válido');
      return false;
    }

    const selectedPatient = pacientes.find(
      (patient) => String(patient.id_paciente) === String(selectedPatientId)
    );

    if (!selectedPatient) {
      alert('No se encontró el paciente seleccionado en la lista de pacientes');
      return false;
    }

    const payloadBackend = buildBackendPayload(
      appointmentData,
      selectedPatientId
    );

    const informacion = {
      config,
      payload: payloadBackend
    };

    try {
      const turnoCreado = await dispatch(crearTurnoAction(informacion));

      if (!turnoCreado) {
        closeModal();

        await Swal.fire({
          icon: 'error',
          title: 'No se pudo crear el turno',
          text: 'El backend no devolvió confirmación.',
          confirmButtonText: 'OK',
          allowOutsideClick: false,
          allowEscapeKey: false
        });

        return false;
      }

      closeModal();

      await Swal.fire({
        icon: 'success',
        title: 'La solicitud ha sido un éxito',
        text: turnoCreado?.msg || 'El turno fue creado correctamente.',
        confirmButtonText: 'OK',
        allowOutsideClick: false,
        allowEscapeKey: false
      });

      return true;

    } catch (error) {
      const mensaje =
        error.userMessage ||
        error.response?.data?.msg ||
        error.response?.data?.message ||
        error.response?.data?.error ||
        'No se pudo crear el turno';

      closeModal();

      await Swal.fire({
        icon: 'error',
        title: 'No se pudo crear el turno',
        text: mensaje,
        confirmButtonText: 'OK',
        allowOutsideClick: false,
        allowEscapeKey: false
      });

      return false;
    }
  };

  // Edita un turno existente.
  const handleUpdateAppointment = async (appointmentData) => {
    const appointmentId = appointmentData.id_turno || appointmentData.id;

    const selectedPatientId =
      appointmentData.id_paciente ||
      appointmentData.id_patiente;

    if (!appointmentId) {
      alert('No se encontró el turno que querés editar.');
      return false;
    }

    if (!selectedPatientId) {
      alert('No se encontró el paciente del turno.');
      return false;
    }

    const turnoOriginal = appointments.find((appointmentItem) => {
      const currentId = appointmentItem.id_turno || appointmentItem.id;
      return String(currentId) === String(appointmentId);
    });

    const esTurnoDeSerie = Boolean(turnoOriginal?.repeat_group_id);

    const cambioFechaTurno =
      Boolean(turnoOriginal?.fecha_turno) &&
      Boolean(appointmentData.fecha_turno) &&
      turnoOriginal.fecha_turno !== appointmentData.fecha_turno;

    if (
      esTurnoDeSerie &&
      appointmentData.aplicar_serie_desde_este_turno &&
      cambioFechaTurno &&
      !turnoOriginal?.puede_modificar_condiciones_serie
    ) {
      await Swal.fire({
        icon: 'warning',
        title: 'No se puede cambiar la fecha de esta serie',
        text: 'La fecha de una serie solo puede modificarse desde el turno original si todavía no ocurrió. Para cambiarla, creá una nueva serie y borrá los turnos futuros de la serie anterior.',
        confirmButtonText: 'OK',
        allowOutsideClick: false,
        allowEscapeKey: false
      });

      return false;
    }

    const payloadBackend = buildBackendPayload(
      appointmentData,
      selectedPatientId
    );

    const informacion = {
      config,
      payload: payloadBackend
    };

    try {
      const turnoEditado = await dispatch(editarTurnoAction(informacion));

      if (!turnoEditado) {
        closeModal();

        await Swal.fire({
          icon: 'error',
          title: 'No se pudo editar el turno',
          text: 'El backend no devolvió confirmación.',
          confirmButtonText: 'OK',
          allowOutsideClick: false,
          allowEscapeKey: false
        });

        return false;
      }

      closeModal();

      await Swal.fire({
        icon: 'success',
        title: 'Turno editado',
        text: turnoEditado?.msg || 'El turno fue editado correctamente.',
        confirmButtonText: 'OK',
        allowOutsideClick: false,
        allowEscapeKey: false
      });

      return true;

    } catch (error) {
      const mensaje =
        error.userMessage ||
        error.response?.data?.msg ||
        error.response?.data?.message ||
        error.response?.data?.error ||
        'No se pudo editar el turno';

      closeModal();

      await Swal.fire({
        icon: 'error',
        title: 'No se pudo editar el turno',
        text: mensaje,
        confirmButtonText: 'OK',
        allowOutsideClick: false,
        allowEscapeKey: false
      });

      return false;
    }
  };

  // SweetAlert con z-index alto para que aparezca por encima del modal.
  const fireSwalAboveModal = (options) => {
    return Swal.fire({
      ...options,
      target: document.body,
      heightAuto: false,
      didOpen: () => {
        const swalContainer = document.querySelector('.swal2-container');

        if (swalContainer) {
          swalContainer.style.zIndex = '2147483647';
        }
      }
    });
  };

  // Borra un turno o una serie futura.
  // Si el turno ya comenzó o tiene notificación procesada,
  // el modal directamente no ofrece el botón de borrar.
  const handleDeleteAppointment = async ({
    id_turno,
    aplicar_serie_desde_este_turno = false,
    repeat_group_id = null,
    fecha_turno = null
  }) => {
    if (!id_turno) {
      alert('No se encontró el turno que querés borrar.');
      return false;
    }

    const isSeriesDelete = Boolean(aplicar_serie_desde_este_turno && repeat_group_id);

    const confirmacion = await fireSwalAboveModal({
      icon: 'warning',
      title: isSeriesDelete
        ? '¿Querés borrar esta serie?'
        : '¿Querés borrar este turno?',
      text: isSeriesDelete
        ? 'Se borrará este turno y todos los turnos futuros de la misma serie. Los turnos anteriores no se modificarán.'
        : 'El turno dejará de verse en el calendario, pero quedará registrado en la base de datos.',
      showCancelButton: true,
      confirmButtonText: isSeriesDelete
        ? 'Sí, borrar serie'
        : 'Sí, borrar turno',
      cancelButtonText: 'Cancelar',
      allowOutsideClick: false,
      allowEscapeKey: false
    });

    if (!confirmacion.isConfirmed) {
      return false;
    }

    const payloadBackend = {
      id_turno,
      estado: 'BORRADO',
      aplicar_serie_desde_este_turno: isSeriesDelete,
      repeat_group_id,
      fecha_turno,
      fecha_turno_desde: fecha_turno
    };

    const informacion = {
      config,
      payload: payloadBackend
    };

    try {
      const turnoBorrado = await dispatch(borrarTurnoAction(informacion));

      if (!turnoBorrado) {
        await Swal.fire({
          icon: 'error',
          title: 'No se pudo borrar el turno',
          text: 'El backend no devolvió confirmación.',
          confirmButtonText: 'OK',
          allowOutsideClick: false,
          allowEscapeKey: false
        });

        return false;
      }

      closeModal();

      await Swal.fire({
        icon: 'success',
        title: isSeriesDelete ? 'Serie borrada' : 'Turno borrado',
        text: turnoBorrado?.msg || 'La operación se realizó correctamente.',
        confirmButtonText: 'OK',
        allowOutsideClick: false,
        allowEscapeKey: false
      });

      return true;

    } catch (error) {
      const mensaje =
        error.userMessage ||
        error.response?.data?.msg ||
        error.response?.data?.message ||
        error.response?.data?.error ||
        'No se pudo borrar el turno';

      await Swal.fire({
        icon: 'error',
        title: 'No se pudo borrar el turno',
        text: mensaje,
        confirmButtonText: 'OK',
        allowOutsideClick: false,
        allowEscapeKey: false
      });

      return false;
    }
  };

  // Decide si el modal crea o edita según el modo actual.
  const handleSubmitAppointment = async (appointmentData) => {
    if (modalMode === 'edit') {
      return handleUpdateAppointment(appointmentData);
    }

    return handleCreateAppointment(appointmentData);
  };

  // Abre creación desde una celda horaria específica.
  const handleTimeSlotClick = (date, timeSlot) => {
    const formattedDate = formatDateLocal(date);
    const endTimeString = calculateEndTime(timeSlot, 30);

    setModalMode('create');
    setAppointmentToEdit(null);

    setModalPrefilledData({
      day: formattedDate,
      startTime: timeSlot,
      endTime: endTimeString
    });

    setIsModalOpen(true);
  };

  const handleResendNotification = async (payload) => {
    if (!config) {
      await Swal.fire({
        icon: 'error',
        title: 'No se pudo reenviar',
        text: 'No se encontró la configuración de autenticación.',
        confirmButtonText: 'OK'
      });

      return false;
    }

    const informacion = {
      config,
      payload
    };

    try {
      const respuesta = await dispatch(
        reenviarNotificacionTurnoAction(informacion)
      );

      if (!respuesta?.ok) {
        await Swal.fire({
          icon: 'error',
          title: 'No se pudo reenviar',
          text: respuesta?.mensaje || 'El backend no devolvió confirmación.',
          confirmButtonText: 'OK'
        });

        return false;
      }

      // Refrescamos desde backend para que el turno vuelva como PROGRAMADA
      // y no quede ninguna información vieja de REBOTADO / ERROR en pantalla.
      await dispatch(cargarTurnosAction({ config }));

      closeModal();

      await Swal.fire({
        icon: 'success',
        title: 'Notificación reprogramada',
        text: respuesta.mensaje || 'La notificación fue puesta nuevamente en cola.',
        confirmButtonText: 'OK',
        allowOutsideClick: false,
        allowEscapeKey: false
      });

      return true;

    } catch (error) {
      const mensaje =
        error.userMessage ||
        error.response?.data?.msg ||
        error.response?.data?.message ||
        error.response?.data?.error ||
        'Error interno al volver a poner la notificación en cola.';

      await Swal.fire({
        icon: 'error',
        title: 'No se pudo reenviar',
        text: mensaje,
        confirmButtonText: 'OK',
        allowOutsideClick: false,
        allowEscapeKey: false
      });

      return false;
    }
  };

  // Renderiza la vista actual: Mes, Semana o Día.
  const renderCurrentView = () => {
    // Si todavía no llegó la configuración desde backend/Redux,
    // no renderizamos Semana/Día porque necesitan calendarViewSettings.
    if (!calendarViewSettings) {
      return (
        <div style={{ padding: '24px', color: '#666' }}>
          {calendarViewSettingsLoading
            ? 'Cargando configuración del calendario...'
            : 'Preparando configuración del calendario...'}
        </div>
      );
    }

    switch (currentView) {
      case VIEW_TYPES.MONTH:
        return isMobile ? (
          <MobileMonthView
            monthDays={monthDays}
            totalRows={totalRows}
            highlightDay={highlightDay}
            appointments={appointments}
            referenceDate={currentDate}
            onDayClick={(date) => {
              handleSelectDate(date);
              setCurrentView(VIEW_TYPES.DAY);
            }}
          />
        ) : (
          <MonthView
            monthDays={monthDays}
            totalRows={totalRows}
            highlightDay={highlightDay}
            appointments={appointments}
            referenceDate={currentDate}
            onDayClick={(date) => {
              handleSelectDate(date);
              setCurrentView(VIEW_TYPES.DAY);
            }}
          />
        );

      case VIEW_TYPES.WEEK:
        return isMobile ? (
          <MobileWeekView
            weekDays={weekDays}
            selectedDate={selectedDate}
            weekAppointments={weekAppointments}
            onTimeSlotClick={handleTimeSlotClick}
            onStatusChange={handleStatusChange}
            onAppointmentClick={openEditModal}
          />
        ) : (
          <WeekView
            weekDays={weekDays}
            selectedDate={selectedDate}
            weekAppointments={weekAppointments}
            onTimeSlotClick={handleTimeSlotClick}
            onStatusChange={handleStatusChange}
            onAppointmentClick={openEditModal}
            onAddAppointment={openModal}
            onOpenSettings={openSettingsModal}
            calendarViewSettings={calendarViewSettings}
          />
        );

      case VIEW_TYPES.DAY:
        return (
          <DayView
            daySelectorDays={daySelectorDays}
            selectedDate={selectedDate}
            dayAppointments={dayAppointments}
            onSelectDate={handleSelectDate}
            onStatusChange={handleStatusChange}
            onAppointmentClick={openEditModal}
            onTimeSlotClick={handleTimeSlotClick}
            onAddAppointment={() => openCreateModalForDate(selectedDate)}
            onOpenSettings={openSettingsModal}
            calendarViewSettings={calendarViewSettings}
          />
        );

      default:
        return null;
    }
  };

  return (
    <Cuerpo>
      <HeaderUno />

      <HeaderDos
        componente="Calendario"
        paciente={false}
      />

      <CalendarMainContainer>
        <CalendarWrapper>
          <CalendarContainer>
            <CalendarHeader
              currentView={currentView}
              currentMonthLabel={currentMonthLabel}
              weekRangeLabel={weekRangeLabel}
              selectedDayNumber={selectedDayNumber}
              onGoToToday={handleGoToToday}
              onPrevPeriod={handlePrevPeriod}
              onNextPeriod={handleNextPeriod}
              onViewChange={setCurrentView}
              onAddAppointment={openModal}
              consultoriosProfesional={consultoriosProfesional}
            />

            <CalendarSection>
              {renderCurrentView()}
            </CalendarSection>
          </CalendarContainer>
        </CalendarWrapper>
      </CalendarMainContainer>

      <CalendarModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onSubmit={handleSubmitAppointment}
        onDelete={handleDeleteAppointment}
        onResendNotification={handleResendNotification}
        onOpenClinicalHistory={handleOpenClinicalHistory}
        onOpenPatientStats={handleOpenPatientStats}
        prefilledData={modalPrefilledData}
        mode={modalMode}
        appointmentToEdit={appointmentToEdit}
        canDeleteAppointment={
          appointmentToEdit
            ? !turnoYaComenzoOPaso({
                fecha_turno: appointmentToEdit.fecha_turno,
                hora_inicio: appointmentToEdit.hora_inicio
              }) &&
              !tieneNotificacionProcesada(appointmentToEdit)
            : true
        }
      />

      <CalendarSettingsModal
        isOpen={isSettingsModalOpen}
        onClose={closeSettingsModal}
        onSave={handleSaveCalendarViewSettings}
        currentSettings={calendarViewSettings}
      />
    </Cuerpo>
  );
};

export default Calendario;